/**
 * FiberCableProcessor.js - Processeur principal pour les fichiers Fiber Cable Distrib
 */

import { readExcelFile, extractExcelFileInfo } from "./excelReader.js";
import {
  extractBpoFromCableId,
  extractReferenceFromCableId,
  extractTiroirModuleFromCableId,
} from "./fiberCableUtilities.js";
import { PositionsProcessor } from "./PositionsProcessor.js";
import { AttenuationsProcessor } from "./AttenuationsProcessor.js";
import { LengthsProcessor } from "./LengthsProcessor.js";

class FiberCableProcessor {
  constructor() {
    this.workbook = null;
    this.positionsProcessor = null;
    this.attenuationsProcessor = null;
    this.lengthsProcessor = null;
  }

  async processFile(file) {
    try {
      // 1. Lire le fichier Excel
      const excelResult = await readExcelFile(file);
      if (!excelResult.isValid) {
        return {
          isValid: false,
          errors: excelResult.errors,
          data: null,
        };
      }

      this.workbook = excelResult.workbook;

      // 2. Traiter la feuille Positions (obligatoire)
      const positionsResult = await this.processPositions();
      if (!positionsResult.isValid) {
        return {
          isValid: false,
          errors: positionsResult.errors,
          data: null,
        };
      }
      // 3. Traiter la feuille Atténuations (optionnelle)
      const attenuationsResult = await this.processAttenuations();

      // 4. Traiter la feuille Longueurs (optionnelle)
      const lengthsResult = await this.processLengths();

      // 5. Merger toutes les données
      const processResult = this.processEnhancedDistanceAverages(
        positionsResult.data,
        attenuationsResult.data || [],
        lengthsResult.data || []
      );

      if (!processResult.isValid) {
        return {
          isValid: false,
          errors: processResult.errors,
          data: null,
        };
      }

      // 6. Construire les données finales
      const transformedData = this.buildTransformedData(processResult.processedData, file);
      return {
        isValid: true,
        errors: [],
        warnings: processResult.errors,
        data: transformedData,
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Erreur de traitement Fiber Cable Distrib: ${error.message}`],
        data: null,
      };
    }
  }

  async processPositions() {
    this.positionsProcessor = new PositionsProcessor(this.workbook);

    const sheetResult = this.positionsProcessor.findSheet();
    if (!sheetResult.isValid) {
      return { isValid: false, errors: sheetResult.errors, data: null };
    }

    const columnsResult = this.positionsProcessor.detectColumns();
    if (!columnsResult.isValid) {
      return { isValid: false, errors: columnsResult.errors, data: null };
    }

    const extractResult = this.positionsProcessor.extractData();
    if (!extractResult.isValid) {
      return { isValid: false, errors: extractResult.errors, data: null };
    }

    return { isValid: true, errors: [], data: extractResult.data };
  }

  async processAttenuations() {
    this.attenuationsProcessor = new AttenuationsProcessor(this.workbook);

    const sheetResult = this.attenuationsProcessor.findSheet();
    if (!sheetResult.isValid) {
      return { isValid: false, errors: sheetResult.errors, data: null };
    }

    const columnsResult = this.attenuationsProcessor.detectColumns();
    if (!columnsResult.isValid) {
      return { isValid: false, errors: columnsResult.errors, data: null };
    }

    const extractResult = this.attenuationsProcessor.extractData();
    if (!extractResult.isValid) {
      return { isValid: false, errors: extractResult.errors, data: null };
    }

    return { isValid: true, errors: [], data: extractResult.data };
  }

  async processLengths() {
    this.lengthsProcessor = new LengthsProcessor(this.workbook);

    const sheetResult = this.lengthsProcessor.findSheet();
    if (!sheetResult.isValid) {
      return { isValid: false, errors: sheetResult.errors, data: null };
    }

    const columnsResult = this.lengthsProcessor.detectColumns();
    if (!columnsResult.isValid) {
      return { isValid: false, errors: columnsResult.errors, data: null };
    }

    const extractResult = this.lengthsProcessor.extractData();
    if (!extractResult.isValid) {
      return { isValid: false, errors: extractResult.errors, data: null };
    }

    return { isValid: true, errors: [], data: extractResult.data };
  }

  processEnhancedDistanceAverages(positionData, attenuationData, lengthData) {
    const errors = [];

    if (!positionData || !Array.isArray(positionData)) {
      errors.push("Données de positions invalides");
      return { isValid: false, errors, processedData: [] };
    }

    try {
      // Grouper les données de positions par ID_CABLE + N_FIBRE
      const fiberGroups = {};

      positionData.forEach((row) => {
        const key = `${row.ID_CABLE}-${row.N_FIBRE}`;

        // si l'id n'existe pas deja on le crée
        if (!fiberGroups[key]) {
          fiberGroups[key] = {
            id_cable: row.ID_CABLE,
            n_fibre: row.N_FIBRE,
            rows: [],
            positions: {},
          };
        }
        fiberGroups[key].rows.push(row.EXCEL_ROW);

        //fiberGroups[key].positions[row.LAMBDA] = row.DISTANCE;
        if (!fiberGroups[key].positions[row.LAMBDA]) {
          fiberGroups[key].positions[row.LAMBDA] = [];
        }

        fiberGroups[key].positions[row.LAMBDA].push(row.POSITIONS);
      });

      // Indexer les données d'atténuations par clé
      const attenuationIndex = {};
      if (attenuationData && Array.isArray(attenuationData)) {
        attenuationData.forEach((row) => {
          const key = `${row.ID_CABLE}-${row.N_FIBRE}-${row.LAMBDA}`;
          //attenuationIndex[key] = row.EVENTS;
          if (!attenuationIndex[key]) {
            attenuationIndex[key] = [];
          }
          attenuationIndex[key].push(row.EVENTS);
        });
      }

      // Indexer les données de longueurs par clé
      const lengthIndex = {};
      if (lengthData && Array.isArray(lengthData)) {
        lengthData.forEach((row) => {
          const key = `${row.ID_CABLE}-${row.N_FIBRE}-${row.LAMBDA}`;
          //lengthIndex[key] = row.EVENTS;
          if (!lengthIndex[key]) {
            lengthIndex[key] = [];
          }
          lengthIndex[key].push(row.EVENTS);
        });
      }

      const processedData = [];

      // Traiter chaque groupe de fibre
      Object.keys(fiberGroups).forEach((key) => {
        const group = fiberGroups[key];
        const positions = group.positions;

        // Vérifier qu'on a bien les deux longueurs d'onde
        const has1310 = positions.hasOwnProperty(1310);
        const has1550 = positions.hasOwnProperty(1550);

        if (!has1310 || !has1550) {
          errors.push(`Fibre ${key}: longueurs d'onde incomplètes (1310: ${has1310}, 1550: ${has1550})`);
          return;
        }

        const positions1310 = positions[1310].flat();
        const positions1550 = positions[1550].flat();
        // Calculate average for each wavelength, then average between wavelengths
        const maxPostion1310 = Math.max(...positions1310);
        const maxPostion1550 = Math.max(...positions1550);
        const averageMaxPostion = (maxPostion1310 + maxPostion1550) / 2;
        const roundedaverageMaxPostion = Math.ceil(averageMaxPostion);
        const maxPositionsGap = Math.abs(maxPostion1310 - maxPostion1550);

        // Extraire BPO, tiroir et module
        const bpo = extractBpoFromCableId(group.id_cable);
        const { tiroir, module } = extractTiroirModuleFromCableId(group.id_cable);

        // Récupérer les événements
        const attKey1310 = `${group.id_cable}-${group.n_fibre}-1310`;
        const attKey1550 = `${group.id_cable}-${group.n_fibre}-1550`;
        const attEvents1310 = attenuationIndex[attKey1310] || [];
        const attEvents1550 = attenuationIndex[attKey1550] || [];
        const lengthEvents1310 = lengthIndex[attKey1310] || [];
        const lengthEvents1550 = lengthIndex[attKey1550] || [];

        processedData.push({
          REFERENCE: extractReferenceFromCableId(group.id_cable),
          PORT: group.n_fibre,
          DISTANCE_ITE: roundedaverageMaxPostion,
          BPO: bpo,
          TIROIR: tiroir,
          MODULE: module,
          EXCEL_ROWS: group.rows,

          // Données d'événements
          ATTENUATION_EVENTS_1310: attEvents1310,
          ATTENUATION_EVENTS_1550: attEvents1550,
          LENGTH_EVENTS_1310: lengthEvents1310,
          LENGTH_EVENTS_1550: lengthEvents1550,

          // Debug info
          POSITIONS_1310: positions[1310],
          POSITIONS_1550: positions[1550],
          DISTANCE_AVERAGE: roundedaverageMaxPostion,
          DISTANCE_ECART: maxPositionsGap,

          // ID complet pour traçabilité
          ID_CABLE_COMPLET: group.id_cable,
        });
      });

      return {
        isValid: true,
        errors: errors,
        processedData: processedData,
      };
    } catch (error) {
      errors.push(`Erreur de traitement des moyennes: ${error.message}`);
      return { isValid: false, errors, processedData: [] };
    }
  }

  buildTransformedData(processedData, file) {
    const fileInfo = extractExcelFileInfo(file);
    return {
      fileName: file.name,
      fileSize: file.size,
      fileSizeFormatted: fileInfo.sizeFormatted,
      fileLastModified: file.lastModified,
      fileCreationDate: fileInfo.lastModifiedFormatted,

      metadata: {
        validatedAt: new Date().toISOString(),
        processingTimestamp: Date.now(),
      },

      references: processedData,
      referenceCount: processedData.length,
    };
  }
}

// Fonction utilitaire pour usage direct
export async function processFiberCableFile(file) {
  const processor = new FiberCableProcessor();
  return await processor.processFile(file);
}
